import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'

const EASE = [0.16, 1, 0.3, 1]

const PLATFORMS = ['Windows', 'macOS', 'Linux', 'iPhone', 'iPad', 'Android']

/** One line of the headline: it rises out of an invisible mask, the way film titles do. */
function RiseLine({ children, delay, reduceMotion }) {
  return (
    <span className="block overflow-hidden pb-[0.14em]">
      <motion.span
        className="block"
        initial={reduceMotion ? false : { y: '112%' }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  )
}

function FadeUp({ children, delay, reduceMotion, className = '' }) {
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  )
}

/**
 * Opening scene of the download page. The unboxed kit floats in the dark behind the headline: it
 * is dimmed and desaturated, dissolves into the page through a radial mask (no frame, no edge) and
 * drifts a little slower than the page while you scroll. `children` is the install call to action;
 * `notices` (install steps, messages) sits on its own rows below it.
 */
export default function DownloadHero({ children, notices = null }) {
  const reduceMotion = useReducedMotion()
  const sectionRef = useRef(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const driftY = useTransform(scrollYProgress, [0, 1], ['0%', '12%'])

  return (
    <section
      ref={sectionRef}
      className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden lg:justify-center"
    >
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
        <div className="relative mx-auto h-full max-w-[1500px]">
          <motion.div
            className="absolute inset-x-0 top-[56px] h-[46svh] lg:inset-y-0 lg:left-auto lg:right-[-22%] lg:top-0 lg:h-full lg:w-[min(1120px,82%)]"
            style={reduceMotion ? undefined : { y: driftY }}
          >
            <div className="m1nt-art-stage absolute inset-0">
              <motion.img
                src="/home/download-kit.jpg"
                width="1024"
                height="576"
                alt=""
                fetchPriority="high"
                decoding="async"
                draggable="false"
                className="m1nt-art-dim absolute inset-0 block h-full w-full select-none object-cover"
                initial={reduceMotion ? false : { opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 2.4, ease: EASE }}
              />
              <div className="m1nt-grain absolute inset-0" />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-[var(--gutter)] pb-14 pt-[calc(56px+env(safe-area-inset-top,0px)+40svh)] lg:pb-24 lg:pt-[calc(56px+env(safe-area-inset-top,0px)+48px)]">
        <FadeUp delay={0.5} reduceMotion={reduceMotion}>
          <p className="font-m1-mono text-[12px] uppercase tracking-[0.2em] text-m1-ink-soft">
            M1NT app
          </p>
        </FadeUp>

        <h1 className="mt-5 font-m1-display text-[clamp(44px,7.2vw,104px)] font-medium leading-[1.02] tracking-[-0.015em] text-m1-ink sm:font-normal">
          <RiseLine delay={0.7} reduceMotion={reduceMotion}>
            Your collection,
          </RiseLine>
          <RiseLine delay={0.85} reduceMotion={reduceMotion}>
            one tap away.
          </RiseLine>
        </h1>

        <FadeUp delay={1.2} reduceMotion={reduceMotion}>
          <p className="mt-6 max-w-[28em] text-[19px] font-light leading-relaxed text-m1-ink-soft">
            Install M1NT on your computer in one click, or scan the code to add it to your phone or
            tablet. No app store, nothing to download by hand.
          </p>
        </FadeUp>

        <FadeUp delay={1.4} reduceMotion={reduceMotion} className="mt-9 space-y-5">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
            {children}
            <a
              href="#phone"
              className="inline-flex min-h-[44px] items-center gap-2 text-[14px] tracking-[0.04em] text-m1-ink-soft transition-colors hover:text-m1-ink"
            >
              Add to your phone
              <i className="ri-arrow-down-line text-base" aria-hidden="true" />
            </a>
          </div>
          {notices}
        </FadeUp>

        <FadeUp delay={1.7} reduceMotion={reduceMotion} className="mt-14 lg:mt-20">
          <ul className="flex flex-wrap gap-x-7 gap-y-2 border-t border-m1-line pt-5 font-m1-mono text-[11px] uppercase tracking-[0.18em] text-m1-muted">
            {PLATFORMS.map((platform) => (
              <li key={platform}>{platform}</li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>
  )
}
